import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Typography } from '@mui/material';
import { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Resolver, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Button } from 'src/components/button';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { Section } from 'src/components/section';
import { routes } from 'src/constants/routes';
import { supabaseClient } from 'src/auth/supabase';
import { styles } from 'src/pages/set-password/styles';
import {
    setPasswordDataDefaultValues,
    setPasswordDataKeys,
} from 'src/pages/set-password/constants';
import { setPasswordDataSchema } from 'src/pages/set-password/setPasswordDataSchema';
import { SetPasswordFormValues } from 'src/pages/set-password/types';
import { useUpdatePasswordSupabase } from 'src/auth/hooks/use-update-password-supabase';

export const ResetPasswordPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const {
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<SetPasswordFormValues>({
        resolver: yupResolver(setPasswordDataSchema) as Resolver<SetPasswordFormValues>,
        defaultValues: setPasswordDataDefaultValues,
        mode: 'onChange',
    });

    const {
        mutateAsync: updatePassword,
        isPending,
        isError,
        error,
        isSuccess,
        reset,
    } = useUpdatePasswordSupabase();

    useEffect(() => {
        const init = async () => {
            const { data } = await supabaseClient.auth.getSession();
            if (!data.session) navigate(routes.summary.index);
        };

        init();
    }, [navigate]);

    const onSubmit = handleSubmit(async ({ password }) => {
        reset();
        await updatePassword({ password });
    });

    return (
        <Box css={styles.container}>
            <Box css={styles.imageContainer} />

            <Box css={styles.wrapper}>
                {isSuccess ? (
                    <Section header={t('forgotten_password_success_title')}>
                        <Typography variant="body3" css={styles.subtitle} sx={{ mb: 4 }}>
                            {t('forgotten_password_reset_success')}
                        </Typography>

                        <Button
                            variant="contained"
                            css={styles.cta}
                            onClick={() => navigate(routes.summary.index)}
                        >
                            {t('forgotten_password_go_to_homepage')}
                        </Button>
                    </Section>
                ) : (
                    <Fragment>
                        <Section header={t('forgotten_password_title')}>
                            <Typography variant="body3" css={styles.subtitle}>
                                {t('forgotten_password_subtitle')}
                            </Typography>
                        </Section>

                        {isError && (
                            <Typography color="error">
                                {error?.message || t('forgotten_password_error_generic')}
                            </Typography>
                        )}

                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={setPasswordDataKeys.password}
                            control={control}
                            label={t('forgotten_password_new_password')}
                        />

                        <ControlledField
                            fieldType={FieldTypes.Password}
                            name={setPasswordDataKeys.confirmPassword}
                            control={control}
                            label={t('forgotten_password_confirm_new_password')}
                        />

                        <Button
                            variant="contained"
                            css={styles.cta}
                            type="submit"
                            loading={isPending}
                            onClick={onSubmit}
                            disabled={isSubmitting || isPending}
                        >
                            {t('forgotten_password_cta')}
                        </Button>
                    </Fragment>
                )}
            </Box>
        </Box>
    );
};
